<script setup>
import { computed, ref } from "vue";
import slider from "vue3-slider";
import arrow from "@/assets/img/up-right-arrow.png";
import waImg from "@/assets/img/whatsapp.png";
import useUtil from "@/composables/useUtil";
import appApi from "@/services/api";
import { exceptionRequestMsg, showToast } from "@/assets/js/util";

const axios = appApi();

const pending = ref(true);
const listaEnganches = ref([]);
const listaResiduales = ref([]);
const clienteDefault = ref(null);
const consultando = ref(false);
const cotizacionPrevia = ref(null);
const descargando = ref(false);
const mostrarSeccionDescarga = ref(false);
const tokenCotizacion = ref(null);

const init = async () => {
  try {
    const res = await axios.get(`public/cotizaciones/calculadora`);

    const { cliente, enganches, residuales } = res.data;

    listaEnganches.value = enganches;
    listaResiduales.value = residuales;
    clienteDefault.value = cliente;
    pending.value = false;
  } catch (error) {
    exceptionRequestMsg(error);
  }
};

init();

const { numberFormat, baseURL } = useUtil();

const state = ref({
  monto: 7375000,
  descripcion: "",
  plazo: 12,
  enganche: 15,
  residual: 5,
  correo: null,
  celular: null,
});

const montoDinamico = computed(() => {
  return numberFormat(state.value.monto);
});

const consultar = async () => {
  if (state.value.descripcion == "")
    return showToast("Ingrese el activo que desea arrendar", "info");
  consultando.value = true;
  tokenCotizacion.value = null;
  try {
    const resp = await axios.post(
      `public/cotizaciones/calculadora/${clienteDefault.value.id}/calcular`,
      {
        method: "POST",
        ...state.value,
      }
    );

    cotizacionPrevia.value = resp.data;
  } catch (error) {
    exceptionRequestMsg(error);
  } finally {
    consultando.value = false;
  }
};

const toggleSeccionDescarga = () => {
  if (!cotizacionPrevia.value) return;

  mostrarSeccionDescarga.value = true;
};

const descargar = async () => {
  if (!state.value.celular || !state.value.correo || !state.value.descripcion)
    return showToast("El celular y correo son obligatorios", "info");
  descargando.value = true;
  try {
    const resp = await axios.post(`public/cotizaciones/calculadora/store`, {
      method: "POST",
      ...state.value,
      ...clienteDefault.value,
      importe: state.value.monto,
      estado: 7, // Coahuila,
      municipio: 252, // Torreón,
      rfc: "NA",
      empresa_proveedor: "NA",
      empresa_celular: "1000000000",
      agente_nombre: "NA",
      agente_celular: "1000000000",
      arrendamiento: 5, // Equipo Industrial,
      moneda: "MXN",
      user_id: 111, // Usuario cotización pública
      cliente: clienteDefault.value.id,
      celular: state.value.celular,
      correo: state.value.correo,
    });

    tokenCotizacion.value = resp.data.cotizacion.token;
  } catch (error) {
    exceptionRequestMsg(error);
  } finally {
    descargando.value = false;
  }
};

const urlDescarga = computed(() => {
  const plazo12 = ref("00");
  const plazo24 = ref("00");
  const plazo36 = ref("00");
  const plazo48 = ref("00");
  const plazo60 = ref("00");

  if (state.value.plazo == 12) {
    plazo12.value = 12;
  }

  if (state.value.plazo == 24) {
    plazo12.value = 24;
  }

  if (state.value.plazo == 36) {
    plazo12.value = 36;
  }

  if (state.value.plazo == 48) {
    plazo12.value = 48;
  }

  if (state.value.plazo == 60) {
    plazo12.value = 60;
  }

  const url = `${baseURL.value}public/cotizaciones/${tokenCotizacion.value}/preview?plazo=${plazo12.value}${plazo24.value}${plazo36.value}${plazo48.value}${plazo60.value}`;
  return url;
});
</script>

<template>
  <section
    class="card d-flex flex-column justify-content-center align-center shadow-xl my-4 overflow-hidden text-dark"
    style="border-radius: 50px"
  >
    <div class="px-4 py-3">
      <h6
        class="text-uppercase m-0 text-center fw-bold my-4"
        style="font-size: 14px; letter-spacing: 0.7px"
      >
        Simulador Ventus Leasing
      </h6>

      <div class="row flex-row py-3">
        <div
          class="col-12 col-sm-6 d-flex flex-column justify-content-center align-center"
        >
          <span>¿Qué equipo quieres arrendar?</span>
        </div>
        <div class="col-12 col-sm-6">
          <input
            class="form-control mt-3 bg-light"
            placeholder="Tesla Model X 2024"
            autocomplete="off"
            v-model="state.descripcion"
            :disabled="pending"
          />
        </div>
      </div>

      <div class="">
        <p class="fw-bold">Valor del activo</p>

        <div>
          <div
            class="d-flex d-md-none flex-row justify-content-between align-center"
          >
            <small style="font-size: 10px">$250,000 MXN</small>
            <small style="font-size: 10px">$15,000,000 MXN</small>
          </div>
          <slider
            color="#f7bf00"
            track-color="#EAEAEA"
            v-model="state.monto"
            :min="250000"
            :max="15000000"
            :tooltip="true"
            tooltipText="%v"
            @drag-end="consultar"
          />
          <div class="d-flex flex-row justify-content-between align-center">
            <small class="d-none d-md-flex" style="font-size: 10px"
              >$250,000 MXN</small
            >
            <span class="mt-3 fw-bold d-none d-md-flex"
              >${{ montoDinamico }} MXN</span
            >
            <small class="d-none d-md-flex" style="font-size: 10px"
              >$15,000,000 MXN</small
            >
          </div>
          <div
            class="d-flex d-md-none flex-row justify-content-center align-center"
          >
            <span class="mt-3 fw-bold">${{ state.monto }} MXN</span>
          </div>
        </div>
      </div>

      <div class="row mt-5 gap-3 gap-md-0">
        <div class="col-12 col-md-4 row m-0">
          <div class="col-5 col-md-12">
            <span>Plazo</span>
          </div>
          <div class="col-7 col-md-12">
            <select
              class="form-control bg-light"
              v-model.number="state.plazo"
              :disabled="pending"
              @change="consultar"
            >
              <option :value="12">12 MESES</option>
              <option :value="24">24 MESES</option>
              <option :value="36">36 MESES</option>
              <option :value="48">48 MESES</option>
              <option :value="60">60 MESES</option>
            </select>
          </div>
        </div>
        <div class="col-12 col-md-4 row m-0">
          <div class="col-5 col-md-12">
            <span>Pago inicial</span>
          </div>
          <div class="col-7 col-md-12">
            <select
              class="form-control bg-light"
              v-model.number="state.enganche"
              :disabled="pending"
              @change="consultar"
            >
              <option
                :value="porcentaje"
                v-for="{ id, porcentaje } in listaEnganches"
                :key="id"
              >
                {{ porcentaje }}%
              </option>
            </select>
          </div>
        </div>
        <div class="col-12 col-md-4 row m-0">
          <div class="col-5 col-md-12">
            <span>Valor residual</span>
          </div>
          <div class="col-7 col-md-12">
            <select
              class="form-control bg-light"
              v-model.number="state.residual"
              :disabled="pending"
              @change="consultar"
            >
              <option
                :value="residual"
                v-for="{ id, residual } in listaResiduales"
                :key="id"
              >
                {{ residual }}%
              </option>
            </select>
          </div>
        </div>
      </div>

      <div class="row m-0 py-4 text-center">
        <span class="fw-bold">Tu renta mensual:</span>
        <div
          class="bg-warning col-8 col-md-6 p-3 rounded-3 mx-auto my-4 shadow"
          :class="consultando ? 'bg-opaco' : ''"
        >
          <div
            class="spinner-border text-dark spinner-border-sm p-2"
            role="status"
            v-show="consultando"
          >
            <span class="sr-only">Loading...</span>
          </div>
          <h5 class="m-0 text-dark" v-show="!consultando">
            {{
              cotizacionPrevia
                ? `$ ${cotizacionPrevia.renta_mensual} MXN*`
                : "-"
            }}
          </h5>
        </div>

        <div class="row">
          <span
            class="text-uppercase border-bottom font-bold col-8 col-md-5 mx-auto border-dark"
            role="button"
            @click="toggleSeccionDescarga"
            style="font-size: 14px; letter-spacing: 1px; font-weight: bold"
            v-show="cotizacionPrevia"
            >Descarga tu esquema</span
          >
        </div>

        <div class="row mt-5 gap-3 gap-md-0" v-show="mostrarSeccionDescarga">
          <div class="col-12 col-md-6 row m-0">
            <div class="col-5 col-md-12">
              <span>Correo electrónico</span>
            </div>
            <div class="col-7 col-md-12">
              <input
                type="email"
                autocomplete="off"
                class="form-control bg-light"
                placeholder="correo@ejemplo.com"
                v-model.trim="state.correo"
              />
            </div>
          </div>
          <div class="col-12 col-md-6 row m-0">
            <div class="col-5 col-md-12">
              <span>Celular</span>
            </div>
            <div class="col-7 col-md-12">
              <input
                autocomplete="off"
                type="tel"
                class="form-control bg-light"
                placeholder="10 dígitos"
                v-model.number="state.celular"
                maxlength="10"
              />
            </div>
          </div>
          <div class="col-12 mt-3">
            <button
              class="btn btn-warning text-dark btn-lg"
              type="button"
              :disabled="descargando"
              @click="descargar"
              v-show="tokenCotizacion ? false : true"
            >
              <span
                class="spinner-border spinner-border-sm me-2"
                role="status"
                aria-hidden="true"
                v-show="descargando"
              ></span>
              <span>Descargar</span>
            </button>
          </div>
          <a
            :href="urlDescarga"
            target="_blank"
            class="btn btn-link text-dark btn-lg"
            v-show="tokenCotizacion ? true : false"
            ><i class="ri-file-download-line me-2"></i>Descargar cotización</a
          >
        </div>

        <span class="text-xxs mt-5 px-md-5">
          * Los datos presentados en esta simulación son para uso informativo y
          pueden variar en la cotización formal. Los valores finales están
          sujetos a aprobación crediticia y condiciones específicas del
          arrendamiento.
        </span>
      </div>
    </div>
    <div class="row m-0">
      <div
        class="col-6 bg-dark text-white py-4 d-grid grid-col align-center justify-content-center p-0"
      >
        <img width="30" class="mx-auto" :src="arrow" />
        <span class="mt-3 fw-bold fs-5 fs-md-1">Empieza aquí</span>
        <a
          href="https://ventusleasing.mx/pre-aprueba-tu-linea-paso-1-crea-tu-cuenta-o-inicia-sesion/"
          class="text-reset"
          target="_blank"
        >
          <span
            class="text-uppercase font-bold col-11 col-md-9 mx-auto border-dark w-100"
            style="font-size: 9px; letter-spacing: 1px; font-weight: bold"
            ><u>Pre-aprueba tu línea</u></span
          >
        </a>
      </div>
      <div
        class="col-6 bg-light py-4 d-grid grid-col align-center justify-content-center p-0"
      >
        <img width="30" class="mx-auto" :src="waImg" alt="" />
        <span class="mt-3 fw-bold fs-5 fs-md-1">¿Tienes dudas?</span>
        <a
          href="https://api.whatsapp.com/send/?phone=%2B528715060593&text&type=phone_number&app_absent=0"
          class="text-reset"
          target="_blank"
        >
          <span
            class="text-uppercase font-bold col-8 col-md-6 mx-auto border-dark w-100"
            style="font-size: 9px; letter-spacing: 1px; font-weight: bold"
            ><u>Contacta a un asesor</u></span
          >
        </a>
      </div>
    </div>
  </section>
</template>

<style scoped>
.bg-dark {
  background: #000 !important;
}

.text-dark {
  color: #000 !important;
}

.bg-warning {
  background: #f7bf00 !important;
}

.bg-opaco {
  opacity: 0.6;
}
</style>
